import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { t } from '../../i18n';

/**
 * ArrowScrollRow (Marcel 2026-09-24): JEDE horizontal scrollbare Tab-/Chip-Leiste
 * bekommt links und rechts runde Pfeil-Buttons – „zu viele verstehen nicht, dass es
 * nach rechts hin noch mehr Tabs gibt“. Die Pfeile sind IMMER sichtbar (auch am
 * Desktop), am Anfang/Ende nur gedimmt, nie versteckt. Ein Klick scrollt um `step`
 * Pixel (Default: 60 % der sichtbaren Breite). Zustand über scroll-Listener +
 * ResizeObserver (fail-soft, wenn es den im Test nicht gibt).
 *
 * Test-IDs: `${testId}-prev`, `${testId}-next`, `${testId}-scroller`.
 *
 * `overlay` (Marcel 2026-09-26, Startseite „Darts in deiner Stadt“ + Lösungen):
 * Inhalt beginnt bündig links, die Pfeile liegen als runde Buttons AUF den
 * Rändern und erscheinen nur, wenn es in ihre Richtung weitergeht („der
 * Nach-links-Button macht keinen Sinn, wenn noch nicht gescrollt wurde“).
 * Tab-Leisten bleiben beim Standard (Pfeile immer sichtbar, gedimmt).
 */
export interface ArrowScrollRowProps {
  children: React.ReactNode;
  /** Klassen am äußeren Wrapper (flex-Zeile: Pfeil · Scroller · Pfeil) */
  className?: string;
  /** Klassen am Scroller (z. B. gap-6, snap-x); overflow/scrollbar kommen von hier */
  scrollClassName?: string;
  /** Scrollschritt in px; ohne Angabe 60 % der sichtbaren Breite */
  step?: number;
  testId?: string;
  /** Element des Scrollers – `ul` für Listen mit `li`-Kindern */
  as?: 'div' | 'ul';
  role?: string;
  ariaLabel?: string;
  /** Externer Ref auf den Scroller (z. B. für „aktiven Tab in Sicht scrollen“) */
  scrollRef?: React.Ref<HTMLElement>;
  /** Pfeile kleiner (h-7) für dichte Leisten */
  compact?: boolean;
  /** Zusätzliche Klassen an beiden Pfeilen */
  arrowClassName?: string;
  /** Ein Kind soll `data-scroll-item` tragen, dann ist der Schritt eine Item-Breite */
  stepByItem?: boolean;
  /** data-testid des äußeren Wrappers (Default: testId) – abweichend, wenn der Elternknoten dieselbe Id trägt */
  wrapperTestId?: string;
  /** Pfeile als Overlay auf den Rändern, nur in scrollbarer Richtung; Inhalt bündig links */
  overlay?: boolean;
}

const SCROLLER_BASE =
  'min-w-0 flex-1 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

/** PURE: kann in die jeweilige Richtung gescrollt werden? */
export function arrowState(scrollLeft: number, clientWidth: number, scrollWidth: number): { canPrev: boolean; canNext: boolean } {
  return {
    canPrev: scrollLeft > 1,
    canNext: scrollLeft + clientWidth < scrollWidth - 1,
  };
}

const ArrowScrollRow: React.FC<ArrowScrollRowProps> = ({
  children,
  className = '',
  scrollClassName = '',
  step,
  testId = 'arrow-scroll-row',
  as = 'div',
  role,
  ariaLabel,
  scrollRef,
  compact = false,
  arrowClassName = '',
  stepByItem = false,
  wrapperTestId,
  overlay = false,
}) => {
  const innerRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState({ canPrev: false, canNext: false });

  const update = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const next = arrowState(el.scrollLeft, el.clientWidth, el.scrollWidth);
    setState((prev) => (prev.canPrev === next.canPrev && prev.canNext === next.canNext ? prev : next));
  }, []);

  useLayoutEffect(() => {
    update();
  });

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', update, { passive: true });
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => update());
      observer.observe(el);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', update);
    }
    return () => {
      el.removeEventListener('scroll', update);
      if (observer) observer.disconnect();
      else if (typeof window !== 'undefined') window.removeEventListener('resize', update);
    };
  }, [update]);

  const scrollBy = (direction: 1 | -1) => {
    const el = innerRef.current;
    if (!el) return;
    let delta = step;
    if (delta == null && stepByItem) {
      const item = el.querySelector<HTMLElement>('[data-scroll-item]');
      if (item) delta = item.getBoundingClientRect().width + 12;
    }
    if (delta == null) delta = Math.max(120, Math.round(el.clientWidth * 0.6));
    if (typeof el.scrollBy === 'function') el.scrollBy({ left: direction * delta, behavior: 'smooth' });
    else el.scrollLeft += direction * delta;
  };

  const size = compact ? 'h-7 w-7' : 'h-8 w-8';
  const arrowCls = (enabled: boolean) =>
    `flex ${size} shrink-0 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60 ${
      enabled ? '' : 'opacity-40'
    } ${arrowClassName}`;

  const overlayArrowCls = `absolute top-1/2 z-10 flex ${size} -translate-y-1/2 items-center justify-center rounded-full bg-black/85 text-zinc-200 shadow-md ring-1 ring-zinc-800 transition hover:bg-zinc-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60 ${arrowClassName}`;

  const scrollerProps: Record<string, unknown> = {
    ref: (el: HTMLElement | null) => {
      innerRef.current = el;
      setRef(scrollRef, el);
    },
    className: `${SCROLLER_BASE} ${scrollClassName}`.trim(),
    'data-testid': `${testId}-scroller`,
  };
  if (role) scrollerProps.role = role;
  if (ariaLabel) scrollerProps['aria-label'] = ariaLabel;

  if (overlay) {
    return (
      <div className={`relative ${className}`.trim()} data-testid={wrapperTestId ?? testId}>
        {React.createElement(as, scrollerProps, children)}
        {state.canPrev && (
          <button
            type="button"
            aria-label={t('web.component.edit-shared.tabs-nach-links-scrollen')}
            onClick={() => scrollBy(-1)}
            className={`${overlayArrowCls} left-0`}
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft size={compact ? 15 : 17} />
          </button>
        )}
        {state.canNext && (
          <button
            type="button"
            aria-label={t('web.component.edit-shared.tabs-nach-rechts-scrollen')}
            onClick={() => scrollBy(1)}
            className={`${overlayArrowCls} right-0`}
            data-testid={`${testId}-next`}
          >
            <ChevronRight size={compact ? 15 : 17} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`.trim()} data-testid={wrapperTestId ?? testId}>
      <button
        type="button"
        aria-label={t('web.component.edit-shared.tabs-nach-links-scrollen')}
        aria-disabled={!state.canPrev}
        onClick={() => scrollBy(-1)}
        className={arrowCls(state.canPrev)}
        data-testid={`${testId}-prev`}
      >
        <ChevronLeft size={compact ? 15 : 17} />
      </button>
      {React.createElement(as, scrollerProps, children)}
      <button
        type="button"
        aria-label={t('web.component.edit-shared.tabs-nach-rechts-scrollen')}
        aria-disabled={!state.canNext}
        onClick={() => scrollBy(1)}
        className={arrowCls(state.canNext)}
        data-testid={`${testId}-next`}
      >
        <ChevronRight size={compact ? 15 : 17} />
      </button>
    </div>
  );
};

export default ArrowScrollRow;
